import React, {useState, useContext, useEffect} from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import {
    NumberSymbolRegular,
    PlayingCardsRegular,
} from '@fluentui/react-icons';
import {
    Suits1, HonorCardsNoTens, 
    SuitSymbols, SuitColors,
    DealSeats, CardSeats, TableSeats, CardSeatsStringMap,
    asDealSeats, asCardSeats,
} from './BridgeTypes';
import { DeckStateContext, Cards } from './BridgeEngineDeck';
import { GameSettingsContext } from './BridgeEngineSettings';
import { PlayStateContext, TurnMode, SeatsMatchScore, Match0 } from './BridgeEnginePlay';
import Card from './Card';
import './Hand.css'

export function renderSuit(suit: string): JSX.Element {
    return (
        <span className="suit-suit" style={{
            color: SuitColors[Suits1.indexOf(suit)]
        }}>
            {SuitSymbols[Suits1.indexOf(suit)]}
        </span>
    )
}

interface HandProps {
    seat: CardSeats;
}

const Hand: React.FC<HandProps> = ({ seat }) => {
    const gameSettingsContext = useContext(GameSettingsContext)!;
    const { gameSettings } = gameSettingsContext;

    const deckContext = useContext(DeckStateContext)!;
    const [deck, setDeck] = useState<Cards[]>(deckContext.deck);
    
    const playStateContext = useContext(PlayStateContext)!;
    const { turnState } = playStateContext;

    const [playerSeat, setPlayerSeat] = useState<DealSeats>(playStateContext.boardState.playerSeat);
    const [matchScores, setMatchScores] = useState<SeatsMatchScore>(Match0());

    useDeepCompareEffect(() => {
        setDeck(deckContext.deck);
    }, [deckContext.deck]);

    useDeepCompareEffect(() => {
        setMatchScores(turnState.matchScores);
    }, [turnState.matchScores]);    
    
    useEffect(() => {
        setPlayerSeat(playStateContext.boardState.playerSeat);
    }, [playStateContext.boardState.playerSeat]);

    const testPointInDiv = (div: HTMLDivElement, location: DOMPoint): boolean => {
        const rect = div.getBoundingClientRect();
        return location.x >= rect.x && location.x <= rect.x + rect.width &&
            location.y >= rect.y && location.y <= rect.y + rect.height;
    };

    const testCardCountInDiv = (seat: DealSeats): boolean => {
        const cardCount = deck!.filter((card) => asDealSeats(card.seat) === seat).length;
        // must be less than 13 to add a card.
        return cardCount < 13;
    };

    const handleCardDragEnd = (id: string, newOrigin: DOMPoint) => {
        const card: Cards | undefined = deck!.find((card) => card.id === id);
        if (!card) return;
        if (!newOrigin) return;

        let foundSeat = false;
        const tableGridContainer = document.getElementById('bridge-table-grid-container') as HTMLDivElement;
        if (tableGridContainer) {
            for (const seat of TableSeats) {
                const seatDiv = tableGridContainer.querySelector(`#item-seat-${seat}`) as HTMLDivElement;
                if (seatDiv && testPointInDiv(seatDiv, newOrigin) && testCardCountInDiv(seat)) {
                    if (seat !== playerSeat && deckContext.updateCardSeat) {
                        deckContext.updateCardSeat(card.id, asCardSeats(seat));
                    }
                    foundSeat = true;
                    break;
                }
            }
        }
        if (!foundSeat && deckContext.updateCardSeat) {
            deckContext.updateCardSeat(card.id, CardSeats.Off);
        }
    };

    const renderSeatHeader = (seat: CardSeats): JSX.Element => {
        return (
            <>
                { CardSeatsStringMap[seat]}
            </>
        );
    }

    const renderSeatMatchInfo = (seat: CardSeats): JSX.Element | null => {
        if (seat === 'off') {
            return null;
        }
        if (asDealSeats(seat) === playerSeat) {
            return (
                <span className="seat-match">
                    {playerSeat === asDealSeats(seat) ? '(You)' : ''}
                </span>
            );
        }

        return (
            <div className="seat-match">
                <div className="seat-matchAll">{matchScores[seat]['all']}&#10004;</div>
                <div className="seat-matchRank">R{matchScores[seat]['rank']}</div>
                <div className="seat-matchSuit">S{matchScores[seat]['suit']}</div>
            </div>
        );
    };

    const renderCards = (seat: CardSeats, suit: string): JSX.Element => {
        const suitLength = deck.filter((card: Cards) => card.seat === seat && card.suit === suit).length;
        const emptySuit = suitLength === 0;

        let missingCards = 0;
        const handSeat = asDealSeats(seat);
        if (handSeat && playStateContext.boardState.deal[handSeat] && gameSettings.showSuitDepth) {
            const cardsInSuit = playStateContext.boardState.deal[handSeat].filter((card: string) => card[1] === suit).length || 0;
            missingCards = cardsInSuit - suitLength;
        }        

        const grayCard = (card: Cards): boolean => {
            return gameSettings.honorsOnly && !(HonorCardsNoTens.includes(card.rank) || (gameSettings.tenIsHonor && card.rank === 'T'));
        }

        // only playerSeat can't drag cards while playing.
        const canDrag: boolean = !(asDealSeats(seat) === playerSeat || playStateContext.turnState.playMode !== TurnMode.Playing);
        return (
            <>
                <div className="suit-card">
                    {deck
                        .filter((card: Cards) => card.seat === seat && card.suit === suit)
                        .map((card: Cards) => (
                            <Card key={card.id}
                                id={card.id}
                                suit={card.suit}
                                rank={card.rank}
                                canDrag={canDrag && !card.revealed}
                                makeGray={grayCard(card)}
                                revealed={card.revealed}
                                onCardDragEnd={handleCardDragEnd}
                            />
                        ))}
                    {Array.from({ length: missingCards }).map((_, index) => (
                        <PlayingCardsRegular key={index} className="suit-card-space" />
                    ))}
                </div>
                {emptySuit && missingCards === 0 && (<div className="suit-card-dash">-</div>)}
            </>
        )
    }

    return (
        <div id="hand-grid-container">
            <div id="hand-header" className="seat-header">
                <div className="seat-seat">{renderSeatHeader(seat)}</div>
                <div className="seat-match">{renderSeatMatchInfo(seat)}</div>
            </div>
            <div id="hand-suit-s">{renderSuit('s')}</div>
            <div id="hand-suit-h">{renderSuit('h')}</div>
            <div id="hand-suit-d">{renderSuit('d')}</div>
            <div id="hand-suit-c">{renderSuit('c')}</div>
            <div id="hand-cards-s" className="card-row">
                {renderCards(seat, 's')}
            </div>
            <div id="hand-cards-h" className="card-row">
                {renderCards(seat, 'h')}
            </div>
            <div id="hand-cards-d" className="card-row">
                {renderCards(seat, 'd')}
            </div>
            <div id="hand-cards-c" className="card-row">
                {renderCards(seat, 'c')}
            </div>
        </div>
    );
};

export default Hand;